// Thin auth wrapper. Uses real Clerk when REACT_APP_CLERK_PUBLISHABLE_KEY is set;
// otherwise provides a mock context so the app is usable end-to-end in preview.
import React from "react";
import {
    ClerkProvider,
    SignedIn,
    SignedOut,
    useAuth as useClerkAuth,
    useUser as useClerkUser,
    SignIn as ClerkSignIn,
    SignUp as ClerkSignUp,
    UserButton as ClerkUserButton,
} from "@clerk/clerk-react";
import { setTokenGetter } from "./api";

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "";
export const USE_CLERK = !!CLERK_KEY;

// ---- Mock provider (used when no Clerk key) ----
const MockAuthContext = React.createContext({
    isSignedIn: true,
    user: { id: "u-mock", primaryEmailAddress: { emailAddress: "you@spotcar.dev" }, firstName: "Demo", lastName: "Buyer", imageUrl: "" },
});

function MockClerkProvider({ children }) {
    const value = React.useMemo(() => ({
        isSignedIn: true,
        user: {
            id: "u-mock",
            primaryEmailAddress: { emailAddress: "you@spotcar.dev" },
            firstName: "Demo",
            lastName: "Buyer",
            imageUrl: "",
        },
    }), []);
    return (
        <MockAuthContext.Provider value={value}>
            {children}
        </MockAuthContext.Provider>
    );
}

export function AuthProvider({ children }) {
    React.useEffect(() => {
        if (!USE_CLERK) setTokenGetter(async () => "dev-token");
    }, []);
    if (USE_CLERK) {
        return (
            <ClerkProvider
                publishableKey={CLERK_KEY}
                signInUrl="/sign-in"
                signUpUrl="/sign-up"
                afterSignInUrl="/app/dashboard"
                afterSignUpUrl="/app/dashboard"
                appearance={{
                    variables: { colorPrimary: "#FFCC00" },
                    elements: {
                        formButtonPrimary:
                            "bg-[#FFCC00] !text-[#000] hover:bg-[#e6b800] rounded-none font-mono uppercase tracking-widest text-xs",
                        card: "border-2 border-black rounded-none shadow-brutal",
                        socialButtonsBlockButton:
                            "border-2 border-black rounded-none font-mono uppercase tracking-widest text-xs",
                    },
                }}
            >
                <ClerkTokenBridge />
                {children}
            </ClerkProvider>
        );
    }
    return <MockClerkProvider>{children}</MockClerkProvider>;
}

function ClerkTokenBridge() {
    const { getToken, isSignedIn } = useClerkAuth();
    React.useEffect(() => {
        if (isSignedIn) {
            setTokenGetter(async () => (await getToken()) || "");
        }
    }, [getToken, isSignedIn]);
    return null;
}

export function ExtensionAuthBridge() {
    if (!USE_CLERK) return null;
    return <ExtensionAuthBridgeInner />;
}

function ExtensionAuthBridgeInner() {
    const { getToken, isSignedIn } = useClerkAuth();
    React.useEffect(() => {
        if (!isSignedIn) {
            window.postMessage({ type: "CARCHECK_AUTH_SIGNOUT" }, window.location.origin);
            return;
        }
        let active = true;
        const pushToken = async () => {
            const token = await getToken();
            if (token && active) {
                window.postMessage({ type: "CARCHECK_AUTH_TOKEN", token }, window.location.origin);
            }
        };
        pushToken();
        const interval = setInterval(pushToken, 50_000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [isSignedIn, getToken]);
    return null;
}

// ---- Unified hooks ----
export function useAuth() {
    if (USE_CLERK) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const a = useClerkAuth();
        return { isSignedIn: !!a.isSignedIn, isLoaded: a.isLoaded };
    }
    return { isSignedIn: true, isLoaded: true };
}

export function useUser() {
    if (USE_CLERK) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const u = useClerkUser();
        return u.user || null;
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return React.useContext(MockAuthContext).user;
}

// ---- Guard components ----
export function RequireAuth({ children }) {
    if (USE_CLERK) {
        return (
            <>
                <SignedIn>{children}</SignedIn>
                <SignedOut>
                    <RedirectToSignIn />
                </SignedOut>
            </>
        );
    }
    return children;
}

function RedirectToSignIn() {
    React.useEffect(() => {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = `/sign-in?redirect_url=${encodeURIComponent(returnTo)}`;
    }, []);
    return null;
}

// ---- Sign-in / Sign-up screens ----
export function SignInScreen() {
    if (USE_CLERK) {
        const params = new URLSearchParams(window.location.search);
        const redirectUrl = params.get("redirect_url");
        return (
            <div className="min-h-screen grid place-items-center bg-white p-8">
                <ClerkSignIn
                    {...(redirectUrl ? { forceRedirectUrl: redirectUrl } : {})}
                    appearance={{
                        elements: {
                            card: "border-2 border-black rounded-none shadow-brutal",
                            formButtonPrimary:
                                "bg-black hover:bg-black text-white rounded-none font-mono uppercase tracking-widest text-xs",
                            socialButtonsBlockButton:
                                "border-2 border-black rounded-none font-mono uppercase tracking-widest text-xs",
                        },
                    }}
                />
            </div>
        );
    }
    return <MockAuthScreen mode="sign-in" />;
}

export function SignUpScreen() {
    if (USE_CLERK) {
        return (
            <div className="min-h-screen grid place-items-center bg-white p-8">
                <ClerkSignUp
                    appearance={{
                        elements: {
                            card: "border-2 border-black rounded-none shadow-brutal",
                            formButtonPrimary:
                                "bg-black hover:bg-black text-white rounded-none font-mono uppercase tracking-widest text-xs",
                        },
                    }}
                />
            </div>
        );
    }
    return <MockAuthScreen mode="sign-up" />;
}

function MockAuthScreen({ mode }) {
    return (
        <div className="min-h-screen grid place-items-center bg-white p-8">
            <div
                className="border-2 border-black bg-white shadow-brutal max-w-md w-full"
                data-testid="mock-auth-card"
            >
                <div className="p-6 border-b-2 border-black bg-[#FFCC00]">
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase">
                        Clerk not configured · preview mode
                    </div>
                    <h2 className="font-display text-3xl uppercase mt-2 leading-none">
                        {mode === "sign-up" ? "Sign up" : "Sign in"}
                    </h2>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm leading-relaxed text-zinc-700">
                        Add <code className="font-mono bg-zinc-100 px-1">REACT_APP_CLERK_PUBLISHABLE_KEY</code> to{" "}
                        <code className="font-mono bg-zinc-100 px-1">/app/frontend/.env</code> and restart the frontend to enable real auth.
                    </p>
                    <p className="text-sm leading-relaxed text-zinc-700">
                        For now you can browse the app as a demo user.
                    </p>
                    <a
                        href="/app/dashboard"
                        data-testid="mock-auth-continue"
                        className="btn-brutal block text-center border-2 border-black bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] shadow-brutal-sm"
                    >
                        Continue as demo →
                    </a>
                </div>
            </div>
        </div>
    );
}

export function UserButton() {
    if (USE_CLERK) {
        return (
            <ClerkUserButton
                afterSignOutUrl="/"
                appearance={{
                    elements: { userButtonAvatarBox: "h-8 w-8 rounded-none border-2 border-black" },
                }}
            />
        );
    }
    return (
        <div
            className="h-8 w-8 border-2 border-black bg-[#FFCC00] grid place-items-center font-display text-xs uppercase"
            data-testid="mock-user-avatar"
        >
            DB
        </div>
    );
}
